import dbStorageService from '../dbStorageService';
import cacheHelper from './cacheHelper';

/**
 * Helper for circle data operations
 */
const circleDataHelper = {
    /**
     * Gets all active circles
     * @returns {Promise<Array>} List of active circles
     */
    async getCircles() {
        try {
            const circles = await dbStorageService.getAllRecords('Circle');
            const activeCircles = circles.filter(circle => !circle.isDeleted);
            return activeCircles;
        } catch (error) {
            console.error('[CircleService] Error getting circles:', error);
            throw new Error(`Failed to get circles: ${error.message}`);
        }
    },

    /**
     * Get a circle by UUID
     * @param {string} circleId - The UUID of the circle to retrieve
     * @returns {Promise<Object>} The circle data
     */
    async getCircleById(circleId) {
        try {
            const circles = await dbStorageService.getRecordsByField('Circle', 'uuid', circleId);
            if (!circles.length) {
                throw new Error('Circle not found');
            }
            return circles[0];
        } catch (error) {
            console.error(`Error getting circle ${circleId}:`, error);
            throw new Error(`Failed to get circle: ${error.message}`);
        }
    },

    /**
     * Creates a new circle
     * @param {Object} circleData - Data for the new circle
     * @param {string} circleData.name - The name of the circle
     * @param {string} [circleData.description] - Description of the circle
     * @param {Array|string} [circleData.members] - Members of the circle
     * @returns {Promise<Object>} The created circle record
     */
    async createCircle(circleData) {
        try {
            // Log members data for debugging
            console.log('[circleDataHelper] Creating circle with members:', circleData.members);
            
            // Ensure we have valid members data
            let membersData = circleData.members || [];
            
            // Check if membersData is a string that contains a stringified array
            if (typeof membersData === 'string') {
                // Check if it's already a stringified string (double-escaped)
                if (membersData.startsWith('"') && membersData.endsWith('"')) {
                    // Remove the outer quotes to prevent double-escaping
                    membersData = membersData.slice(1, -1);
                }
                
                try {
                    // Try to parse the string to get the actual array
                    membersData = JSON.parse(membersData);
                } catch (e) {
                    console.error('Failed to parse members string:', e);
                    membersData = [];
                }
            }
            
            // Ensure members is always an array
            if (!Array.isArray(membersData)) {
                console.warn('Members data is not an array, setting to empty array');
                membersData = [];
            }
            
            const record = {
                uuid: crypto.randomUUID(),
                name: circleData.name,
                description: circleData.description || '',
                members: JSON.stringify(membersData),
                isDeleted: false,
                lastModified: new Date().toISOString()
            };
            
            // Mark as user-initiated to trigger sync backoff
            await dbStorageService.upsertRecords('Circle', [record], null, true);
            
            cacheHelper.clearCache();
            return record;
        } catch (error) {
            console.error('Failed to create circle:', error);
            throw error;
        }
    },

    /**
     * Updates an existing circle
     * @param {string} uuid - The UUID of the circle to update
     * @param {Object} circleData - New data for the circle
     * @returns {Promise<Object>} The updated circle record
     */
    async updateCircle(uuid, circleData) {
        try {
            // Check if circle exists
            const circles = await dbStorageService.getRecordsByField('Circle', 'uuid', uuid);
            if (!circles.length) {
                throw new Error('Circle not found');
            }
            
            const circle = circles[0];
            
            // Process members data similar to create
            let membersData = circleData.members || [];
            
            // Handle cases where membersData might be a string
            if (typeof membersData === 'string') {
                // Check if it's already a stringified string (double-escaped)
                if (membersData.startsWith('"') && membersData.endsWith('"')) {
                    // Remove the outer quotes to prevent double-escaping
                    membersData = membersData.slice(1, -1);
                }
                
                try {
                    // Try to parse the string to get the actual array
                    membersData = JSON.parse(membersData);
                } catch (e) {
                    console.error('Failed to parse members string:', e);
                    // Fall back to existing members
                    try {
                        membersData = JSON.parse(circle.members);
                    } catch (parseError) {
                        console.error('Failed to parse existing members:', parseError);
                        membersData = [];
                    }
                }
            }
            
            // Ensure members is always an array
            if (!Array.isArray(membersData)) {
                console.warn('Members data is not an array');
                // Try to use existing members
                try {
                    membersData = JSON.parse(circle.members);
                } catch (e) {
                    membersData = [];
                }
            }
            
            const updatedCircle = {
                ...circle,
                name: circleData.name || circle.name,
                description: circleData.description || circle.description,
                members: JSON.stringify(membersData),
                lastModified: new Date().toISOString()
            };
            
            // Mark as user-initiated to trigger sync backoff
            await dbStorageService.upsertRecords('Circle', [updatedCircle], null, true);
            
            cacheHelper.clearCache();
            return updatedCircle;
        } catch (error) {
            console.error('Failed to update circle:', error);
            throw error;
        }
    },

    /**
     * Marks a circle as deleted
     * @param {string} circleId - UUID of the circle to delete
     * @returns {Promise<Object>} Result of the operation
     */
    async softDeleteCircle(circleId) {
        try {
            const circles = await dbStorageService.getRecordsByField('Circle', 'uuid', circleId);
            if (!circles.length) {
                throw new Error('Circle not found');
            }
            
            const circle = circles[0];
            
            // Instead of deleting, mark as deleted
            const updatedCircle = {
                ...circle,
                isDeleted: true,
                lastModified: new Date().toISOString()
            };
            
            // Mark as user-initiated to trigger sync backoff
            await dbStorageService.upsertRecords('Circle', [updatedCircle], null, true);
            
            cacheHelper.clearCache();
            return { success: true };
        } catch (error) {
            console.error('Failed to delete circle:', error);
            throw error;
        }
    },

    /**
     * Get a circle by its UUID
     * @param {string} uuid - The UUID of the circle to retrieve
     * @returns {Promise<Object|null>} The circle data or null if not found
     */
    async getCircleByUuid(uuid) {
        try {
            if (!uuid) {
                console.error('[CircleService] Cannot get circle: UUID is required');
                return null;
            }
            
            const circles = await dbStorageService.getAllRecords('Circle');
            const circle = circles.find(c => c.uuid === uuid && !c.isDeleted);
            
            if (!circle) {
                console.warn(`[CircleService] Circle with UUID ${uuid} not found`);
                return null;
            }
            
            return circle;
        } catch (error) {
            console.error(`[CircleService] Error getting circle with UUID ${uuid}:`, error);
            throw new Error(`Failed to get circle: ${error.message}`);
        }
    },

    /**
     * Creates a new circle with a specific UUID (for temporary circles from invitations)
     * @param {Object} circleData - Data for the new circle
     * @param {string} circleData.name - The name of the circle
     * @param {string} circleData.uuid - The UUID to use for the circle
     * @param {string} [circleData.description] - Description of the circle
     * @param {Array|string} [circleData.members] - Members of the circle
     * @returns {Promise<Object>} The created circle record
     */
    async createCircleWithUuid(circleData) {
        try {
            if (!circleData.uuid) {
                throw new Error('UUID is required when creating a circle with a specific UUID');
            }
            
            // Log members data for debugging
            console.log('[circleDataHelper] Creating circle with specific UUID and members:', circleData.members);
            
            // Ensure we have valid members data
            let membersData = circleData.members || [];
            
            // Check if membersData is a string that contains a stringified array
            if (typeof membersData === 'string') {
                // Check if it's already a stringified string (double-escaped)
                if (membersData.startsWith('"') && membersData.endsWith('"')) {
                    // Remove the outer quotes to prevent double-escaping
                    membersData = membersData.slice(1, -1);
                }
                
                try {
                    // Try to parse the string to get the actual array
                    membersData = JSON.parse(membersData);
                } catch (e) {
                    console.error('Failed to parse members string:', e);
                    membersData = [];
                }
            }
            
            // Ensure members is always an array
            if (!Array.isArray(membersData)) {
                console.warn('Members data is not an array, setting to empty array');
                membersData = [];
            }
            
            const record = {
                uuid: circleData.uuid,
                name: circleData.name,
                description: circleData.description || '',
                members: JSON.stringify(membersData),
                isDeleted: false,
                lastModified: new Date().toISOString()
            };
            
            // Mark as temporary record that will be replaced by sync
            record.isTemporary = true;
            
            // Mark as user-initiated to trigger sync backoff
            await dbStorageService.upsertRecords('Circle', [record], null, true);
            
            cacheHelper.clearCache();
            return record;
        } catch (error) {
            console.error('Failed to create circle with specific UUID:', error);
            throw error;
        }
    }
};

export default circleDataHelper; 